import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { CommonModule } from '@angular/common';
import { BaseComponent } from '../../../../shared/utils/base.component';
import { ParticipantLeadInfo } from '../../../../shared/interfaces/participant-lead-info';
import { LeadDetailsService } from '../../../../services/lead-details.service';
import { performValidation } from '../../../../shared/utils/validation.util';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { UserService } from '../../../../services/user.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { AuthorizeDirective } from '../../../../shared/authorization/authorize.directive.';
import { AuthorizeFieldDirective } from '../../../../shared/authorization/authorizeField.directive';
import { AuthorizeFieldViewDirective } from '../../../../shared/authorization/authorizeFieldView.directive';
import { ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS, RADIO_YES_NO_OPTIONS, STATIC_DATA } from '../../../../shared/constants/static-data.constants';
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { NotificationService } from '../../../../shared/services/notification.service';
import { SectionsService } from '../../../../services/sections.service';
import { DatePickerComponent } from '../../../../shared/components/date-picker/date-picker.component';
import { DropdownSelectComponent } from '../../../../shared/components/dropdown-select/dropdown-select.component';
import { MfaService } from '../../../../services/mfa.service';
import { ErrorAlertService } from '../../../../shared/services/error-alert.service';

@Component({
  selector: 'ppl-lead-detail-participant-info',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NzDatePickerModule,
    CommonModule,
    NgZorroModule,
    AuthorizeDirective,
    AuthorizeFieldDirective,
    AuthorizeFieldViewDirective,
    ButtonComponent,
    DatePickerComponent,
    DropdownSelectComponent,
  ],
  templateUrl: './lead-detail-participant-info.component.html',
  styleUrl: './lead-detail-participant-info.component.css',
})
export class LeadDetailParticipantInfoComponent
  extends BaseComponent
  implements OnInit, OnChanges {
  mode = 'read';
  filledInfo!: ParticipantLeadInfo;
  infoForm!: FormGroup;
  contactMethods: any;
  statesList: any;
  genderList: any;
  prefList: any;
  savedData!: ParticipantLeadInfo;
  steps: any;
  approvedStatus: string = STATIC_DATA.APPROVED;
  deniedStatus: string = STATIC_DATA.DENIED;
  leadStatus = '';
  contactStsReasonLkpList: any;
  waiver: any;
  fiEntries: any;
  mcoEntries: any;
  ldssOfficeValues: LookupValueItem[] = [];
  currentFIValues: LookupValueItem[] = [];
  tpsCountries: LookupValueItem[] = [];
  showEdit = false;
  sectionName = '';
  participantRegisterMode: any[] = [];

  @Input() set sectionData(data: any) {
    this.steps = data
  }

  @Input() enrollmentType !: string;

  @Input() entry !: string;
  @Input() pageId = '';
  @Input() tabId = '';
  @Input() programCode !: string;
  @Input() selectedLead = 0;
  @Input() readOnly = false;
  @Input() restrictReferralEdit = false;
  radioOptions = RADIO_YES_NO_OPTIONS;
  relationshipOptions: any;
  eyeColors: any;
  hairColors: any;
  raceOptions: any;
  taxExemptOptions: any;
  @Output() leadUpdated = new EventEmitter<boolean>();
  nprftDocumentationReceivedOptions: any[] = [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }];

  constructor(
    private fb: FormBuilder,
    private leadDetailsService: LeadDetailsService,
    private userService: UserService,
    private dateTimeFormatterService: DateTimeFormatterService,
    private notificationService: NotificationService,
    private sectionService: SectionsService,
    private mfaService: MfaService,
    private errorAlertService: ErrorAlertService
  ) {
    super();
  }


  ngOnInit(): void {

    this.createForm();
    this.getLookupValues();
    this.leadDetailsService.getLeadStatus().subscribe((status: string) => {
      this.leadStatus = status;

    })

    const isConsumer = this.userService.isSelectedRole(['Participant (External)']);
    const isEmployer = this.userService.isSelectedRole(['Employer (External)']);
    const providerType = this.sectionService.getEnrollmentType();
    const isAuthRepOrConsumerEnrollment = ((isEmployer && Array.isArray(providerType) && providerType.includes('authRepresentative')) || isConsumer)
      && this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT;
    this.showEdit = !isAuthRepOrConsumerEnrollment && !this.restrictReferralEdit;
    if (this.mode === 'read') {
      this.leadDetailsService.getUpdatedLeadData().subscribe(
        (data) => {
          if (data?.responsedata?.infoData) {
            this.filledInfo = data?.responsedata?.infoData;
            this.filledInfo.registerPref = data?.responsedata?.communicationPreferences?.registerPref;
            this.savedData = JSON.parse(JSON.stringify(this.filledInfo));
            // this.savedData.registerPref = data?.responsedata?.communicationPreferences?.registerPref;
          } else {
            this.filledInfo = {};
            this.savedData = null as any;
          }

        }
      )
    }

    const program = this.userService.getSelectedProgram();

    this.lookupService.getOrgData(program?.program_code ?? '',).subscribe((data: any) => {
      if (data.status === 200) {
        this.fiEntries = data.responsedata.filter((item: any) => item.orgTypeLookup === 'FI');
        this.mcoEntries = data.responsedata.filter((item: any) => item.orgTypeLookup === 'MCO');
      }
    })
    this.infoForm.get('tempProtectedSts')?.valueChanges.subscribe((value: any) => {
      if (value === 'No') {
        this.infoForm.get('tpsCountryLkp')?.setValue(null);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    try {

      if (changes['sectionData'] && changes['sectionData'].currentValue) {
        this.secData = this.steps['participant_info'];
      }
    } catch (e) {
      console.log('Error in getting section data and updating latest info', e);
    }

  }

  createForm(): void {
    this.infoForm = this.fb.group({
      businessName: [''],
      fname: [''],
      mname: [''],
      lname: [''],
      maiden_name: [''],
      dob: [null],
      eligiblityStartDate: [null],
      eligiblityEndDate: [null],
      gender: [null],
      participant_id: [''],
      contactStsReasonLkp: [null],
      contactStsComments: [''],
      leadId: [],
      state: [''],
      waiver: [null],
      ldssOffice: [],
      currentFI: [],
      mcoOrgName: [null],
      fiOrgName: [null],
      isShowOnDirectory: [],
      isProvidingServices: [null],
      relationToParticipant: [],
      additionalDemographics: [],
      race: [],
      heightFeet: [],
      heightInches: [],
      weight: [],
      eyeColor: [],
      hairColor: [],
      birthPlace: [],
      birthCity: [],
      birthState: [],
      birthCountry: [],
      registerPref: [],
      taxExempt: [],
      nprftDocumentationReceived: [],
      yearsInState: [],
      referralId: [],
      tempProtectedSts: [],
      tpsCountryLkp: [],
      pdrFirstName: [''],
      pdrLastName: [''],
      pdrRelation: [null],
      pdrPhone: ['']
    });
  }

  getLookupValues(): void {
    this.statesList = this.lookupService.getLookupValue('STATE');
    this.genderList = this.lookupService.getLookupValue('Gender');
    this.prefList = this.lookupService.getLookupValue('Participant_Register_Mode');
    this.waiver = this.getLookup('Waiver');
    this.participantRegisterMode = this.getLookup('Participant_Register_Mode');
    this.relationshipOptions = this.getLookup('Relationship');
    this.eyeColors = this.getLookup('Eye Color');
    this.hairColors = this.getLookup('Hair Color');
    this.raceOptions = this.getLookup('Race');
    this.taxExemptOptions = this.getLookup('Tax Exemption');
    this.ldssOfficeValues = this.getLookup('LDSS_Office');
    this.currentFIValues = this.getLookup('CFI');
    this.contactStsReasonLkpList = this.getLookup('contact_status_reason_lkp');
    this.tpsCountries = this.getLookup('Temporary Protected Status Country');
  }

  editPersonalInfo() {
    this.mode = 'edit';
    this.infoForm.controls['leadId'].setValue(this.filledInfo.leadId);


    if (this.filledInfo) {
      this.infoForm.patchValue(this.filledInfo);
      const updateObj: any = {
        'dob': this.dateTimeFormatterService.formatStringToDate(this.filledInfo.dob as string),
        'isShowOnDirectory': this.filledInfo?.isShowOnDirectory === true ? 'Yes' : 'No',
        'isProvidingServices': this.filledInfo?.isProvidingServices === true ? 'Yes'
          : this.filledInfo?.isProvidingServices === false ? 'No'
            : this.filledInfo?.isProvidingServices
      };
      if (this.filledInfo?.eligiblityStartDate) {
        updateObj['eligiblityStartDate'] = this.dateTimeFormatterService.formatStringToDate(this.filledInfo.eligiblityStartDate as string);
      }
      if (this.filledInfo?.eligiblityEndDate) {
        updateObj['eligiblityEndDate'] = this.dateTimeFormatterService.formatStringToDate(this.filledInfo.eligiblityEndDate as string);
      }
      this.infoForm.patchValue(updateObj);
    }
  }

  doMfa() {
    if (this.userService.showMfaPopup()) {
      this.mfaService.doMfa().subscribe(success => {
        if (success) {
          this.editPersonalInfo();
        } else {
           this.errorAlertService.openModal('error', 'Error', 'MFA verification failed or cancelled.');
        }
      });
    } else {
      this.editPersonalInfo();
    }
  }

  cancelEdit() {
    this.mode = 'read';
    this.validationErrors = {};
    this.infoForm.reset();
  }

  save() {
    let formaData = { ...this.infoForm.value };

    formaData.dob = this.dateTimeFormatterService.formatDateToString(this.infoForm.value.dob as Date);
    if (this.infoForm.value?.eligiblityStartDate) {
      formaData.eligiblityStartDate = this.dateTimeFormatterService.formatDateToString(this.infoForm.value.eligiblityStartDate as Date);
    }
    if (this.infoForm.value?.eligiblityEndDate) {
      formaData.eligiblityEndDate = this.dateTimeFormatterService.formatDateToString(this.infoForm.value.eligiblityEndDate as Date);
    }
    try {
      this.validationErrors = performValidation(this.infoForm.value, this.secData, 'Next', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType());
    } catch {
      console.log("Error in validations");
    }

    // save data to server if no validation errors
    if (Object.keys(this.validationErrors).length === 0) {
      let mcoOrgId = null;
      let fiOrgId = null;
      if (this.infoForm.value.mcoOrgName) {
        mcoOrgId = this.mcoEntries.find((entry: { orgName: string; orgId: number }) => entry.orgName === this.infoForm.value.mcoOrgName)?.orgId
          ?? this.filledInfo?.mcoOrgId ?? null;
      }
      if (this.infoForm.value.fiOrgName) {
        fiOrgId = this.fiEntries.find((entry: { orgName: string; orgId: number }) => entry.orgName === this.infoForm.value.fiOrgName)?.orgId
          ?? this.filledInfo?.fiOrgId ?? null;
      }
      formaData = {
        ...formaData,
        fname: formaData.fname.trim(),
        lname: formaData.lname.trim(),
        mcoOrgId,
        fiOrgId,
        modifiedDate: this.filledInfo.modifiedDate
      };

      const payload = this.preparePayload(formaData);
      // save data to server 
      this.leadDetailsService.updateLeadDemographics(payload, this.entry).subscribe({
        next: () => {
          this.leadUpdated.emit(true);
          this.cancelEdit();
        },
        error: () => {
          this.leadUpdated.emit(true);
          this.cancelEdit();
        }
      })
    }
  }

  preparePayload(source: any) {
    const isShowOnDirectory = source.isShowOnDirectory === 'Yes' ? true : false;
    const isProvidingServices = source.isProvidingServices === 'Yes' ? true : source.isProvidingServices === 'No' ? false : source.isProvidingServices;

    if (source.isProvidingServices === 'Yes') {
      this.notificationService.alert(
        'warning',
        'Warning',
        'Please enter Services rendered.'
      );
    } else if (source.isProvidingServices === 'No') {
      this.notificationService.alert(
        'warning',
        'Warning',
        'Please enter SSN.'
      );
    }
    let payload: any = {};
    payload = { ...source, id: this.selectedLead, programCode: this.programCode, isShowOnDirectory: isShowOnDirectory, isProvidingServices: isProvidingServices };

    if (this.entry === STATIC_DATA.PROVIDER_ENROLLMENT) {
      payload = { ...payload, providerType: ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS[this.enrollmentType] }
    }

    return payload;
  }

  getProspectiveDRDisplayValue(data: ParticipantLeadInfo): string {
    const name = [data?.pdrFirstName, data?.pdrLastName].filter(Boolean).join(' ');
    const relationship = data?.pdrRelation
      ? this.getLookupValue(this.relationshipOptions, data.pdrRelation)
      : '';
    const base = (name && relationship) ? `${name} (${relationship})` : (name || relationship || '');
    const phone = data?.pdrPhone;
    return [base, phone].filter(Boolean).join(' – ');
  }
}
